import * as React from 'react';

import { observer } from 'mobx-react';

import { Field, FormInput, FormStore } from '@estee/elc-form';
import { IFormValues } from '@estee/elc-form/typings/views/Form';

import * as classNames from 'classnames';

import {
    DEFAULT_MOBILE_DISPLAY_FORMAT,
    DEFAULT_MOBILE_VALUE_FORMAT,
    INPUT_FIELD_HIGHLIGHTED_CLASS
} from '~constants/Constants';
import { IPhoneNumberInputConfig } from '~types/config';
import { fieldClasses } from '~utils/Classes';
import { FieldValidations } from '~utils/FieldValidations';
import { useRemoveHighlightOnFocus } from '~utils/hooks/field';
import {
    createAsYouType,
    ensureCountryCallingCodePrefix,
    formatValue,
    getCountryCodeByCallingCode,
    getDisplayFieldName
} from '~utils/phone-number-helper';

import type { CountryCallingCode, CountryCode } from 'libphonenumber-js/types';
export const TEST_ID_PHONE_INPUT = 'ramp-phone-input';

export interface IPhoneInputProps extends IPhoneNumberInputConfig {
    inputLabel: string;
    name: string;
    fieldValidations: FieldValidations;
    formStore: FormStore<IFormValues>;
    disabled?: boolean;
    initialValue?: string;
    readonly?: boolean;
    highlighted?: boolean;
}

const useAsYouTypeInput = (countryCodes: CountryCode[] = [], formStore: FormStore<IFormValues>) => {
    const [countryCode, _setCountryCode] = React.useState<CountryCode | undefined>(countryCodes[0]);

    const setCountryCode = React.useCallback((value: CountryCode) => {
        formStore.setFieldValue('_countryCode', value);
        _setCountryCode(value);
    }, []);

    React.useEffect(() => {
        // When `countryCode` field exists, and is set/changed.
        // `countryCode` field value is actually a calling code
        const callingCode = formStore.values?.countryCode;
        if (callingCode) {
            setCountryCode(getCountryCodeByCallingCode(callingCode as CountryCallingCode));
        }
    }, [formStore.values?.countryCode]);

    if (!formStore.values?.countryCode && countryCode) {
        // Store _countryCode in formStore to be used in validation
        formStore.setFieldValue('_countryCode', countryCode);
    }

    // `asYouType` is a libphonenumber-js instance that formats the phone number as the user types.
    // Note: `asYouType.getCountry()` does not always return the correct country code without enough input value.
    return { asYouType: createAsYouType(countryCode), countryCode, setCountryCode };
};

const PhoneInput = observer(function _PhoneInput(props: IPhoneInputProps) {
    const {
        inputLabel,
        name,
        disabled,
        initialValue = '',
        formStore,
        fieldValidations,
        displayFormat = DEFAULT_MOBILE_DISPLAY_FORMAT,
        valueFormat = DEFAULT_MOBILE_VALUE_FORMAT,
        countryCodes = [],
        highlighted = false,
        readonly
    } = props;
    const { isRequired, maxLength, validations } = fieldValidations || {};

    const className = fieldClasses(name, 'phone-input');

    // Create a field to store the display text value of the mobile number field.
    // The actual mobile number value will be stored in form in E.164 format (e.g. +886912345678).
    const displayFieldName = getDisplayFieldName(name);
    const showCountryCallingCode = displayFormat === 'INTERNATIONAL' && countryCodes.length === 1;

    const { asYouType, countryCode } = useAsYouTypeInput(countryCodes, formStore);

    const handleDisplayFieldChange = React.useCallback(
        (value: string) => {
            // No initial country is set, e.g., Hong Kong form without `countryCode` selected
            if (!countryCode) {
                return;
            }

            asYouType.reset();
            const inputValue = asYouType.input(value);
            const phoneNumber = asYouType.getNumber();

            formStore.setFieldValue(name, phoneNumber ? formatValue(phoneNumber, valueFormat) : '');

            let displayValue: string;
            if (showCountryCallingCode) {
                asYouType.reset();
                // We need to show 'fixed' prefix, but the value is missing that part
                const withPrefix = asYouType.input(
                    ensureCountryCallingCodePrefix(value, countryCode)
                );
                formStore.setFieldValue(displayFieldName, withPrefix);
            } else {
                displayValue = phoneNumber?.isValid()
                    ? phoneNumber.format(displayFormat)
                    : inputValue;
                formStore.setFieldValue(displayFieldName, displayValue);
            }
        },
        [asYouType, countryCode]
    );

    React.useEffect(() => {
        const displayFieldValue = formStore.values[displayFieldName] || initialValue;
        handleDisplayFieldChange(displayFieldValue);
    }, [asYouType, countryCode]);

    const onFocus = useRemoveHighlightOnFocus(name);

    return (
        <Field
            name={displayFieldName}
            inputLabel={inputLabel}
            customClassName={classNames(className, {
                [`${INPUT_FIELD_HIGHLIGHTED_CLASS}`]: highlighted
            })}
            isRequired={isRequired}
            disabled={disabled}
            maxLength={maxLength}
            dataTestId={TEST_ID_PHONE_INPUT}
            component={FormInput}
            afterChange={(_displayFieldName: string, value: string) => {
                handleDisplayFieldChange(value);
            }}
            onFocus={onFocus}
            validations={validations}
            readOnly={readonly}
            inputType="tel"
        />
    );
});

export default PhoneInput;
export { PhoneInput };
